import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Trash2, LogOut, Plus, Save, X } from "lucide-react";

const EMPTY = {
  title: "",
  subtitle: "",
  duration: "",
  price_from: 0,
  price_original: 0,
  discount_percent: 0,
  departure_from: "",
  image_url: "",
  badge: "",
  inclusions: [],
  order: 0,
};

const LoginView = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      window.localStorage.setItem("ceo_admin_token", data.token);
      onLogin();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FBFAF6] px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-3xl border border-[#0C2230]/10 bg-white p-8 shadow-xl"
      >
        <h1 className="font-[Fraunces] text-3xl font-medium tracking-tight text-[#0C2230]">
          Admin Login
        </h1>
        <p className="mt-1 text-sm text-[#0C2230]/60">CEO Travel Dashboard</p>
        <div className="mt-6 flex flex-col gap-3">
          <Input
            data-testid="admin-login-email"
            type="email"
            required
            placeholder="admin@ceotravel.id"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-11 rounded-xl"
          />
          <Input
            data-testid="admin-login-password"
            type="password"
            required
            placeholder="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-11 rounded-xl"
          />
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#0C2230] px-5 py-3 text-sm font-semibold text-white hover:bg-[#0a1c27] disabled:opacity-50"
          >
            {loading ? "Memuat…" : "Masuk"}
          </button>
        </div>
      </form>
    </div>
  );
};

const PackageEditor = ({ pkg, onSave, onCancel }) => {
  const [form, setForm] = useState({
    ...EMPTY,
    ...pkg,
    inclusions: pkg?.inclusions?.join("\n") || "",
  });

  const set = (k) => (e) => {
    const numKeys = ["price_from", "price_original", "discount_percent", "order"];
    const v = numKeys.includes(k) ? Number(e.target.value || 0) : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = () => {
    onSave({
      ...form,
      badge: form.badge?.trim() || null,
      departure_from: form.departure_from?.trim() || null,
      price_original: form.price_original || null,
      discount_percent: form.discount_percent || null,
      inclusions: String(form.inclusions || "")
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
    });
  };

  return (
    <div className="rounded-2xl border border-[#0C2230]/10 bg-white p-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Input data-testid="pkg-edit-title" value={form.title} onChange={set("title")} placeholder="Title" />
        <Input data-testid="pkg-edit-subtitle" value={form.subtitle} onChange={set("subtitle")} placeholder="Subtitle (e.g. From Singapore)" />
        <Input data-testid="pkg-edit-duration" value={form.duration} onChange={set("duration")} placeholder="Duration (e.g. 2D1N)" />
        <Input data-testid="pkg-edit-departure" value={form.departure_from || ""} onChange={set("departure_from")} placeholder='Departure from ("Singapore" / "Malaysia")' />
        <Input data-testid="pkg-edit-price" type="number" value={form.price_from} onChange={set("price_from")} placeholder="Price from (IDR)" />
        <Input data-testid="pkg-edit-price-original" type="number" value={form.price_original || 0} onChange={set("price_original")} placeholder="Original price (IDR) — optional" />
        <Input data-testid="pkg-edit-discount" type="number" value={form.discount_percent || 0} onChange={set("discount_percent")} placeholder="Discount % — optional" />
        <Input data-testid="pkg-edit-badge" value={form.badge || ""} onChange={set("badge")} placeholder='Badge (e.g. "Paling Laris")' />
        <Input data-testid="pkg-edit-order" type="number" value={form.order} onChange={set("order")} placeholder="Order" />
        <Input data-testid="pkg-edit-image" className="md:col-span-2" value={form.image_url} onChange={set("image_url")} placeholder="Image URL" />
        <Textarea
          data-testid="pkg-edit-inclusions"
          className="md:col-span-2"
          rows={5}
          value={form.inclusions}
          onChange={set("inclusions")}
          placeholder="Inclusions (one per line)"
        />
      </div>
      <div className="mt-5 flex gap-3">
        <button
          data-testid="pkg-edit-save"
          onClick={submit}
          className="inline-flex items-center gap-2 rounded-full bg-[#0E9A57] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0c8a4d]"
        >
          <Save className="h-4 w-4" /> Simpan
        </button>
        <button
          data-testid="pkg-edit-cancel"
          onClick={onCancel}
          className="inline-flex items-center gap-2 rounded-full border border-[#0C2230]/15 px-5 py-2.5 text-sm font-semibold text-[#0C2230] hover:bg-[#0C2230]/5"
        >
          <X className="h-4 w-4" /> Batal
        </button>
      </div>
    </div>
  );
};

const Dashboard = ({ onLogout }) => {
  const [packages, setPackages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [editing, setEditing] = useState(null); // pkg object or "new"
  const [tab, setTab] = useState("packages");

  const reload = useCallback(async () => {
    try {
      const [pkg, ct, ld] = await Promise.all([
        api.get("/packages"),
        api.get("/contacts"),
        api.get("/leads"),
      ]);
      setPackages(pkg.data);
      setContacts(ct.data);
      setLeads(ld.data);
    } catch (e) {
      console.error("Failed to reload admin data", e);
      toast.error("Gagal memuat data admin");
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const save = async (form) => {
    try {
      if (form.id) {
        await api.put(`/packages/${form.id}`, form);
        toast.success("Paket diperbarui");
      } else {
        await api.post("/packages", form);
        toast.success("Paket dibuat");
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast.error("Gagal menyimpan paket");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus paket ini?")) return;
    try {
      await api.delete(`/packages/${id}`);
      toast.success("Paket dihapus");
      reload();
    } catch (e) {
      toast.error("Gagal menghapus");
    }
  };

  const removeContact = async (id) => {
    if (!window.confirm("Hapus pesan ini?")) return;
    try {
      await api.delete(`/contacts/${id}`);
      reload();
    } catch (e) {
      toast.error("Gagal menghapus");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FBFAF6]">
      <header className="border-b border-[#0C2230]/10 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <div>
            <h1 className="font-[Fraunces] text-2xl font-medium tracking-tight text-[#0C2230]">
              CEO Travel Admin
            </h1>
            <p className="text-xs text-[#0C2230]/55">Manage packages & inquiries</p>
          </div>
          <button
            data-testid="admin-logout"
            onClick={onLogout}
            className="inline-flex items-center gap-2 rounded-full border border-[#0C2230]/15 px-4 py-2 text-xs font-semibold text-[#0C2230] hover:bg-[#0C2230]/5"
          >
            <LogOut className="h-3.5 w-3.5" /> Keluar
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-8 flex gap-2 border-b border-[#0C2230]/10">
          {[
            { id: "packages", label: "Paket" },
            { id: "contacts", label: `Pesan (${contacts.length})` },
            { id: "leads", label: `Lead (${leads.length})` },
            { id: "branding", label: "Branding" },
          ].map((tb) => (
            <button
              key={tb.id}
              data-testid={`admin-tab-${tb.id}`}
              onClick={() => setTab(tb.id)}
              className={`-mb-px border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                tab === tb.id
                  ? "border-[#0E9A57] text-[#0C2230]"
                  : "border-transparent text-[#0C2230]/55 hover:text-[#0C2230]"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {tab === "packages" && (
          <>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-[Fraunces] text-xl font-medium text-[#0C2230]">
                Daftar Paket
              </h2>
              <button
                data-testid="admin-new-package"
                onClick={() => setEditing({ ...EMPTY })}
                className="inline-flex items-center gap-2 rounded-full bg-[#0C2230] px-4 py-2 text-xs font-semibold text-white hover:bg-[#0a1c27]"
              >
                <Plus className="h-3.5 w-3.5" /> Paket Baru
              </button>
            </div>

            {editing && (
              <div className="mb-6">
                <PackageEditor
                  pkg={editing}
                  onSave={save}
                  onCancel={() => setEditing(null)}
                />
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {packages.map((p) => (
                <div
                  key={p.id}
                  data-testid={`admin-package-${p.id}`}
                  className="flex gap-4 rounded-2xl border border-[#0C2230]/10 bg-white p-4"
                >
                  <img
                    src={p.image_url}
                    alt={p.title}
                    className="h-24 w-32 flex-shrink-0 rounded-xl object-cover"
                  />
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-[#0E9A57]">
                          {p.subtitle}
                        </p>
                        <h3 className="font-[Fraunces] text-lg font-medium text-[#0C2230]">
                          {p.title}
                        </h3>
                      </div>
                      {p.badge && (
                        <span className="rounded-full bg-[#B7892F] px-2 py-1 text-[9px] font-semibold uppercase tracking-wide text-white">
                          {p.badge}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-[#0C2230]/70">
                      Rp {Number(p.price_from).toLocaleString("id-ID")} / pax · {p.duration}
                      {p.departure_from ? ` · ${p.departure_from}` : ""}
                      {p.discount_percent ? ` · ${p.discount_percent}% OFF` : ""}
                    </p>
                    <div className="mt-auto flex gap-2 pt-3">
                      <button
                        data-testid={`admin-edit-${p.id}`}
                        onClick={() => setEditing(p)}
                        className="rounded-full bg-[#0C2230] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#0a1c27]"
                      >
                        Edit
                      </button>
                      <button
                        data-testid={`admin-delete-${p.id}`}
                        onClick={() => remove(p.id)}
                        className="inline-flex items-center gap-1 rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-3 w-3" /> Hapus
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "contacts" && (
          <>
            <h2 className="mb-6 font-[Fraunces] text-xl font-medium text-[#0C2230]">
              Pesan Masuk
            </h2>
            {contacts.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-[#0C2230]/20 p-10 text-center text-sm text-[#0C2230]/55">
                Belum ada pesan masuk.
              </p>
            ) : (
              <div className="space-y-3">
                {contacts.map((c) => (
                  <div
                    key={c.id}
                    data-testid={`admin-contact-${c.id}`}
                    className="rounded-2xl border border-[#0C2230]/10 bg-white p-5"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-[Fraunces] text-lg text-[#0C2230]">
                          {c.name}
                        </p>
                        <p className="text-xs text-[#0C2230]/55">
                          {c.email}
                          {c.phone ? ` · ${c.phone}` : ""}
                        </p>
                      </div>
                      <button
                        onClick={() => removeContact(c.id)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Hapus
                      </button>
                    </div>
                    <p className="mt-3 whitespace-pre-wrap text-sm text-[#0C2230]/80">
                      {c.message}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "leads" && (
          <>
            <h2 className="mb-6 font-[Fraunces] text-xl font-medium text-[#0C2230]">
              Quick Leads (Tanya Cepat & WA clicks)
            </h2>
            {leads.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-[#0C2230]/20 p-10 text-center text-sm text-[#0C2230]/55">
                Belum ada lead masuk.
              </p>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-[#0C2230]/10 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-[#FBFAF6] text-left text-[11px] uppercase tracking-[0.18em] text-[#0C2230]/55">
                    <tr>
                      <th className="px-4 py-3">Tanggal</th>
                      <th className="px-4 py-3">Nama</th>
                      <th className="px-4 py-3">WhatsApp</th>
                      <th className="px-4 py-3">Paket</th>
                      <th className="px-4 py-3">Sumber</th>
                      <th className="px-4 py-3">Lang</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l) => (
                      <tr
                        key={l.id}
                        data-testid={`admin-lead-${l.id}`}
                        className="border-t border-[#0C2230]/5"
                      >
                        <td className="px-4 py-3 text-[#0C2230]/65">
                          {new Date(l.created_at).toLocaleString("id-ID")}
                        </td>
                        <td className="px-4 py-3 font-medium text-[#0C2230]">{l.name}</td>
                        <td className="px-4 py-3 text-[#0C2230]/80">{l.phone}</td>
                        <td className="px-4 py-3 text-[#0C2230]/80">
                          {l.package_title || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <span className="rounded-full bg-[#0E9A57]/10 px-2 py-1 text-[10px] font-semibold uppercase text-[#0E9A57]">
                            {l.source || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 uppercase text-[#0C2230]/60">{l.lang || "-"}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={async () => {
                              await api.delete(`/leads/${l.id}`);
                              reload();
                            }}
                            className="text-xs text-red-600 hover:underline"
                          >
                            Hapus
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === "branding" && <BrandingEditor />}
      </div>
    </div>
  );
};

const BrandingEditor = () => {
  const [form, setForm] = useState({ logo_url: "", email: "", phone: "", address: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState("");

  useEffect(() => {
    let mounted = true;
    api
      .get("/settings/branding")
      .then((r) => {
        if (!mounted || !r.data) return;
        setForm({
          logo_url: r.data.logo_url || "",
          email: r.data.email || "",
          phone: r.data.phone || "",
          address: r.data.address || "",
        });
        setPreview(r.data.logo_url || "");
      })
      .catch((err) => console.error("Failed to load branding", err))
      .finally(() => setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("File terlalu besar (max 5 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const original = String(ev.target?.result || "");
      // Compress: load to image, draw to canvas at max 600px wide, output PNG
      const img = new Image();
      img.onload = () => {
        const maxW = 600;
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        // Preserve transparency → PNG. If original looks like jpeg/photo, use jpeg.
        const isPng = original.startsWith("data:image/png") || original.startsWith("data:image/svg");
        const out = isPng ? canvas.toDataURL("image/png") : canvas.toDataURL("image/jpeg", 0.85);
        const sizeKb = Math.round((out.length * 0.75) / 1024);
        if (sizeKb > 90) {
          // Fallback: jpeg quality 0.7 to fit ingress limit
          const out2 = canvas.toDataURL("image/jpeg", 0.7);
          setForm((p) => ({ ...p, logo_url: out2 }));
          setPreview(out2);
        } else {
          setForm((p) => ({ ...p, logo_url: out }));
          setPreview(out);
        }
      };
      img.onerror = () => toast.error("Gagal membaca gambar");
      img.src = original;
    };
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/branding", form);
      toast.success("Branding tersimpan & langsung muncul di website");
    } catch (e) {
      console.error("branding save failed", e);
      if (e?.response?.status === 403 || e?.response?.status === 413) {
        toast.error("File logo terlalu besar. Coba ulang dengan logo yang lebih kecil.");
      } else {
        toast.error(e?.response?.data?.detail || "Gagal menyimpan branding");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-[#0C2230]/55">Memuat...</p>;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="font-[Fraunces] text-xl font-medium text-[#0C2230]">Branding & Kontak</h2>
        <p className="mt-1 text-sm text-[#0C2230]/65">
          Upload logo dan atur email/telepon/alamat. Perubahan langsung muncul di website tanpa redeploy.
        </p>
      </div>

      <div className="rounded-2xl border border-[#0C2230]/10 bg-white p-6">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#0C2230]/70">Logo</p>
        {preview && (
          <div className="mb-4 inline-flex items-center justify-center rounded-xl border border-[#0C2230]/10 bg-[#0C2230] px-6 py-4">
            <img src={preview} alt="logo preview" data-testid="branding-logo-preview" className="h-16 w-auto object-contain" />
          </div>
        )}
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-[#0C2230]/55">Opsi 1: Upload File</label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={handleFile}
              data-testid="branding-logo-upload"
              className="block w-full text-sm text-[#0C2230] file:mr-4 file:rounded-lg file:border-0 file:bg-[#0C2230] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-[#0a1c27]"
            />
            <p className="mt-1 text-xs text-[#0C2230]/55">PNG transparan / JPG / WebP / SVG. Otomatis di-compress ke ~600px.</p>
          </div>
          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-[#0C2230]/10" /></div>
            <div className="relative flex justify-center text-[11px] uppercase"><span className="bg-white px-2 text-[#0C2230]/45">atau</span></div>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-[#0C2230]/55">Opsi 2: Paste URL Gambar</label>
            <Input
              value={form.logo_url?.startsWith("data:") ? "" : (form.logo_url || "")}
              onChange={(e) => {
                const url = e.target.value;
                setForm((p) => ({ ...p, logo_url: url }));
                setPreview(url);
              }}
              data-testid="branding-logo-url"
              placeholder="https://i.imgur.com/abcd.png  atau  https://drive.google.com/..."
            />
            <p className="mt-1 text-xs text-[#0C2230]/55">Bisa upload ke <a href="https://imgbb.com" target="_blank" rel="noreferrer" className="underline">imgbb.com</a> / <a href="https://imgur.com" target="_blank" rel="noreferrer" className="underline">imgur.com</a> dulu, paste URL-nya di sini.</p>
          </div>
          <button
            type="button"
            onClick={() => { setForm((p) => ({ ...p, logo_url: "" })); setPreview("/logo-hero.png"); }}
            data-testid="branding-reset-logo"
            className="text-xs text-[#0C2230]/60 underline hover:text-[#0C2230]"
          >
            Reset ke logo default
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#0C2230]/70">Email</label>
          <Input
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="branding-email"
            placeholder="reservation@ceotravel.tour"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#0C2230]/70">Telepon / WhatsApp</label>
          <Input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            data-testid="branding-phone"
            placeholder="+62 813 9997 828"
          />
        </div>
        <div className="md:col-span-2">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#0C2230]/70">Alamat</label>
          <Textarea
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            rows={3}
            data-testid="branding-address"
            placeholder="Ruko Lotus Garden Blok AA No. 10, Batam Center, Kepulauan Riau"
          />
        </div>
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="branding-save"
        className="inline-flex items-center gap-2 rounded-full bg-[#0E9A57] px-6 py-2.5 text-sm font-semibold text-white shadow hover:bg-[#0c8a4d] disabled:opacity-60"
      >
        <Save className="h-4 w-4" /> {saving ? "Menyimpan..." : "Simpan Branding"}
      </button>
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let mounted = true;
    const token = window.localStorage.getItem("ceo_admin_token");
    if (!token) {
      setChecking(false);
      return;
    }
    api
      .get("/admin/me")
      .then(() => mounted && setAuthed(true))
      .catch(() => {
        window.localStorage.removeItem("ceo_admin_token");
        mounted && setAuthed(false);
      })
      .finally(() => mounted && setChecking(false));
    return () => {
      mounted = false;
    };
  }, []);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FBFAF6] text-sm text-[#0C2230]/60">
        Memuat…
      </div>
    );
  }

  if (!authed) return <LoginView onLogin={() => setAuthed(true)} />;
  return (
    <Dashboard
      onLogout={() => {
        window.localStorage.removeItem("ceo_admin_token");
        setAuthed(false);
      }}
    />
  );
}
